// ============================================================================
// Weekly-goals API — TRAINER-side weekly goal setting (Wave 3).
// ----------------------------------------------------------------------------
// A weekly_goal is the trainer's plan for ONE owned client for ONE week (keyed
// on the Monday week_start): kcal/macro/water targets + a list of exercise
// homework counts ([{type, count}]) + notes. Its set nutrition targets OVERRIDE
// the auto daily targets for that week, so the nutrition endpoints resolve
// source='coach' once a goal is in force.
//   • `forClient` — the goal for ?week= (defaults to the current week).
//   • `upsert`    — PUT the goal (idempotent per client+week); the backend snaps
//     `week` to the Monday and forces trainer_id/client_id from the scope.
// ============================================================================
import { http } from "./http";
import type {
  CalendarWeeklyGoal,
  WeeklyGoalInput,
  WeeklyGoalResponse,
} from "./types";

/** Inclusive [start,end] ISO bounds for the trainer weekly-goal banner feed. */
export interface WeeklyGoalCalendarRange {
  start?: string;
  end?: string;
  // Index signature keeps this assignable to the http `Query` param type.
  [key: string]: string | undefined;
}

export const weeklyGoalsApi = {
  /** An owned client's weekly goal for `?week=` (defaults to the current week). */
  forClient: (clientId: number, week?: string, signal?: AbortSignal) =>
    http.get<WeeklyGoalResponse>(
      `/clients/${clientId}/weekly-goal`,
      { week },
      signal,
    ),

  /**
   * All of THIS trainer's weekly goals (across every client) as week banners —
   * the lime week-level layer of the trainer calendar merge. Each row carries
   * the client name + Monday `week_start`.
   */
  calendar: (range: WeeklyGoalCalendarRange = {}, signal?: AbortSignal) =>
    http.get<CalendarWeeklyGoal[]>("/weekly-goals/calendar", range, signal),

  /** Upsert an owned client's weekly goal (targets + exercise homework + notes). */
  upsert: (clientId: number, body: WeeklyGoalInput) =>
    http.put<WeeklyGoalResponse>(`/clients/${clientId}/weekly-goal`, body),
};
